import {Controller} from "stimulus"

/**
 * Stimulus controller for initializing ECharts charts in the FWA dashboard.
 *
 * Usage in Hiccup:
 *   [:div {:data-controller "fwa-chart"
 *          :data-fwa-chart-options-value (json/write-str chart-options)}
 *    [:div {:data-fwa-chart-target "container"
 *           :style "min-height: 200px; width: 100%;"}]]
 *
 * Requires ECharts to be loaded from CDN before this controller connects.
 */
export default class extends Controller {
  static get values() {
    return {
      options: Object
    }
  }

  static get targets() {
    return ["container"]
  }

  connect() {
    // Wait for next tick to ensure DOM is fully rendered
    setTimeout(() => this.initChart(), 0)

    // Set up IntersectionObserver to detect when element becomes visible
    // This handles cases where the element is initially hidden (e.g., responsive breakpoints)
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.chart) {
            // Element became visible and chart hasn't been initialized yet
            setTimeout(() => this.initChart(), 100)
          } else if (entry.isIntersecting && this.chart && !this.chart.isDisposed()) {
            // Element became visible and chart exists - resize it
            setTimeout(() => this.chart.resize(), 100)
          }
        })
      },
      {threshold: 0.01} // Trigger when at least 1% of element is visible
    )
    this.observer.observe(this.element)
  }

  initChart() {
    // Wait for ECharts to be available (loaded from CDN)
    if (typeof echarts === "undefined") {
      // Check if there's a load event we can listen to
      if (window.echartsLoaded) {
        // Script loaded but echarts not yet available, retry
        if (!this._retryCount) this._retryCount = 0
        if (this._retryCount < 20) {
          this._retryCount++
          setTimeout(() => this.initChart(), 100)
        } else {
          console.error("ECharts script loaded but echarts object not available")
        }
        return
      }
      // Listen for load event if available
      if (typeof window.addEventListener !== "undefined") {
        window.addEventListener(
          "echarts:loaded",
          () => {
            setTimeout(() => this.initChart(), 100)
          },
          {once: true}
        )
      }
      // Retry after a short delay, with max retries
      if (!this._retryCount) this._retryCount = 0
      if (this._retryCount < 50) {
        // Max 5 seconds
        this._retryCount++
        setTimeout(() => this.initChart(), 100)
      } else {
        console.error("ECharts failed to load after 5 seconds")
      }
      return
    }

    // Try to find container target - use Stimulus target first, fallback to manual search
    let containerElement = null
    if (this.hasContainerTarget) {
      containerElement = this.containerTarget
    } else {
      // Fallback: manually find the element with data-fwa-chart-target="container"
      containerElement =
        this.element.querySelector('[data-fwa-chart-target="container"]') ||
        this.element.querySelector("[data-fwa-chart-target=container]") ||
        this.element.querySelector("[data-fwa-chart-target]")

      if (!containerElement) {
        // Try alternative: find by id if chart-id was set
        const chartId = this.element.querySelector("[id]")?.id
        if (chartId) {
          containerElement = document.getElementById(chartId)
        }
      }

      // Last resort: use first child div if it exists
      if (!containerElement && this.element.children.length > 0) {
        containerElement = this.element.children[0]
      }
    }

    if (!containerElement) {
      console.error("fwa-chart: container target not found")
      return
    }

    // Try to get options value - Stimulus should parse it, but fallback to manual read
    let options = this.optionsValue
    if (!options) {
      // Fallback: manually read and parse the data attribute
      const attrValue = this.element.getAttribute("data-fwa-chart-options-value")
      if (attrValue) {
        try {
          options = JSON.parse(attrValue)
        } catch (e) {
          console.error("fwa-chart: Failed to parse options JSON", e)
          return
        }
      } else {
        console.error("fwa-chart: options value not found")
        return
      }
    }

    // Convert formatter strings to functions (ECharts formatters can't be serialized in JSON)
    // Check if formatter is a string that looks like a function definition
    if (options.yAxis && options.yAxis.axisLabel && typeof options.yAxis.axisLabel.formatter === "string") {
      const formatterStr = options.yAxis.axisLabel.formatter.trim()
      if (formatterStr.startsWith("function")) {
        try {
          // Extract function body by removing "function(value) {" from start and "}" from end
          // Format: "function(value) { ... }"
          const paramMatch = formatterStr.match(/function\s*\(([^)]*)\)\s*\{/)
          if (paramMatch) {
            const params = paramMatch[1]
              .split(",")
              .map((p) => p.trim())
              .filter((p) => p)
            // Remove "function(params) {" from start and "}" from end to get body
            const body = formatterStr.replace(/^function\s*\([^)]*\)\s*\{/, "").replace(/\}\s*$/, "")
            options.yAxis.axisLabel.formatter = new Function(...params, body)
          }
        } catch (e) {
          console.warn("fwa-chart: Failed to convert formatter string to function", e)
          // Fallback: remove formatter if conversion fails
          delete options.yAxis.axisLabel.formatter
        }
      }
    }

    try {
      // Ensure container has dimensions before initializing
      // Check both offsetWidth and getBoundingClientRect for more reliable measurement
      const rect = containerElement.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        // Retry after a short delay to allow layout to settle
        // Limit retries to avoid infinite loops
        if (!this._dimensionRetryCount) this._dimensionRetryCount = 0
        if (this._dimensionRetryCount < 20) {
          // Max 2 seconds
          this._dimensionRetryCount++
          setTimeout(() => this.initChart(), 100)
        } else {
          console.error("fwa-chart: Container has no dimensions after retries", {
            width: rect.width,
            height: rect.height,
            offsetWidth: containerElement.offsetWidth,
            offsetHeight: containerElement.offsetHeight
          })
        }
        return
      }

      // Initialize ECharts with SVG renderer for better quality
      this.chart = echarts.init(containerElement, null, {renderer: "svg"})

      // Set the chart options
      this.chart.setOption(options)

      // Force a resize after setting options to ensure proper rendering
      // This is important when the container is initially hidden or has no dimensions
      setTimeout(() => {
        if (this.chart && !this.chart.isDisposed()) {
          this.chart.resize()
        }
      }, 0)

      // Handle window resize
      this.resizeHandler = () => {
        if (this.chart && !this.chart.isDisposed()) {
          this.chart.resize()
        }
      }
      window.addEventListener("resize", this.resizeHandler)
    } catch (error) {
      console.error("fwa-chart: Error initializing chart", error)
    }
  }

  disconnect() {
    // Clean up IntersectionObserver
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    // Clean up resize listener
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
    }

    // Dispose of the chart instance
    if (this.chart && !this.chart.isDisposed()) {
      this.chart.dispose()
    }
  }

  // Allow updating options dynamically
  optionsValueChanged() {
    if (this.chart && !this.chart.isDisposed() && this.optionsValue) {
      this.chart.setOption(this.optionsValue, true)
    }
  }
}
